// İSG uzmanı portalı — Panel B "Son 30 günde açılan izinler" (S34-4, design review
// `S34-carbon-phase2-plan.md` §4 "Panel B", M12 "sr-only veri tablosu"). `permitsCreatedPerDay`
// YOĞUN bir dizidir (§34.1 — `length === 30` HER ZAMAN, eskiden yeniye, bugün DAHİL) — istemci
// eksik gün İCAT ETMEZ/DOLDURMAZ.
//
// **Üç erişilebilirlik katmanı (M12 normatif, "an SVG with no text alternative is a defect"):**
// (1) `<svg aria-hidden>` yalnız GÖRSEL — hiçbir bar `tabIndex` TAŞIMAZ (30 Tab durağı bir
// TUZAK olurdu, design review). (2) Grafiğin ÜSTÜNDE tek cümlelik GÖRÜNÜR özet (`…summary`).
// (3) `<svg>`in HEMEN ALTINDA `sr-only` bir SARMALAYICI `<div>` İÇİNDE `<table>` (30 satır +
// `<caption>`) — ekran okuyucu için TEK gerçek veri kaynağı. Her `<rect>` ayrıca native bir
// `<title>` taşır (işaretçi kullanıcıları için).
//
// **Final QA — `sr-only` doğrudan `<table>` üzerinde DEĞİL, sarmalayıcı `<div>` üzerinde.**
// `sr-only` `width:1px;height:1px;overflow:hidden` verir; ama `table-layout:auto` bir
// `<table>` açık genişliği YOK SAYAR, tarayıcı 30 satırı doğal içerik boyutunda (~334×768px)
// yerleştirir — `position:absolute` olduğundan bu gerçek kutu `clip-path` ile BOYANMASA da
// belge kaydırma alanını uzatır (sayfa sonunda ~700-900px ölü boşluk). Açıkça
// boyutlandırılmış bir `<div>` çocuğunun doğal boyutundan ETKİLENMEZ.
//
// **S34-45 design review M4 — eksen METNİ SVG'DEN ÇIKARILDI.** `preserveAspectRatio="none"` bir
// 732×146 viewBox'ı ~340–540px × 160px'e GERER; bu çubuklar/ızgara ÇİZGİLERİ için zararsızdır
// (bir dikdörtgen dikdörtgen kalır) ama SVG `<text>` için glifleri YATAYDA ~%45–70'e SIKIŞTIRIR —
// 10 birimlik yazı tipi ekranda ~5–7px genişliğe düşer, OKUNAKSIZ hâle gelir (ölçekten bağımsız
// önceki "start/end/middle textAnchor" kırpma düzeltmesinin YERİNE geçer, o düzeltme yalnız
// TAŞMAYI çözüyordu, SIKIŞMAYI değil). Çözüm: çubuklar + ızgara SVG'de KALIR (ızgara çizgileri
// `vectorEffect="non-scaling-stroke"` taşır — gerilmeden bağımsız SABİT çizgi kalınlığı için),
// TÜM metin (Y ekseni sayıları + X ekseni tarihleri) gerçek HTML `<span>`lere taşınır — yüzde
// bazlı konumlanır (`top`/`left`), gerilme SKALASINDAN etkilenmez, `text-xs` (12px) OKUNAKLI
// kalır. Kenar etiketleri yine İÇE büyür (ilk etiket `translate-x-0`, son — bugün —
// `-translate-x-full`, aradakiler `-translate-x-1/2`) — taşma sorunu STRÜKTÜREL olarak
// (viewBox aritmetiğiyle değil, gerçek DOM kutu modeliyle) çözülür.

import type { ExpertDashboardDayCountDto } from '@isgportal/shared';
import { useTranslation } from 'react-i18next';
import { parseCalendarDate } from '../../lib/format';

const PLOT_WIDTH = 700;
const PLOT_HEIGHT = 120;
const TOP_MARGIN = 6;
const VIEWBOX_HEIGHT = TOP_MARGIN + PLOT_HEIGHT;
const BAR_GAP = 4;

/** Y ekseni için "nice" bir üst sınır — 0 ile aynı satırda ikiye bölünebilir tam sayılara döner. */
function niceMax(rawMax: number): number {
  if (rawMax <= 0) {
    return 2;
  }
  if (rawMax <= 4) {
    return rawMax % 2 === 0 ? rawMax : rawMax + 1;
  }
  const magnitude = 10 ** Math.floor(Math.log10(rawMax));
  const normalized = rawMax / magnitude;
  const niceNormalized = normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
}

export function PermitsOpenedChart({
  days,
  culture,
}: {
  /** Yoğun 30 günlük dizi — eskiden yeniye (§34.1). */
  days: ExpertDashboardDayCountDto[];
  culture: string;
}) {
  const { t } = useTranslation();
  const total = days.reduce((sum, d) => sum + d.count, 0);
  const rawMax = days.reduce((max, d) => Math.max(max, d.count), 0);
  const max = niceMax(rawMax);

  const barWidth = (PLOT_WIDTH - (days.length - 1) * BAR_GAP) / days.length;
  const dateFormatter = new Intl.DateTimeFormat(culture, { day: 'numeric', month: 'short' });
  // Takvim günü dizesini (§34.1 `date`, bir UTC ANI DEĞİL) YEREL y/m/d olarak ayrıştırır —
  // görüntüleyenin saat dilimi UTC'nin batısındaysa `new Date(iso + 'T00:00:00Z')` biçimlendirmeyi
  // bir gün geriye kaydırırdı (format.ts `parseCalendarDate`). Geçersiz girdide '—' döner.
  const formatDay = (isoDate: string) => {
    const date = parseCalendarDate(isoDate);
    return date ? dateFormatter.format(date) : '—';
  };
  const todayIndex = days.length - 1;

  const peak = days.reduce(
    (best, d) => (d.count > best.count ? d : best),
    days[0] ?? { date: '', count: 0 },
  );

  const yFor = (count: number) => TOP_MARGIN + PLOT_HEIGHT - (count / max) * PLOT_HEIGHT;

  // BUGÜNDEN GERİYE her 7 günde bir (S34-4 QA düzeltmesi) — ardışık iki görünür etiket HER ZAMAN
  // tam 7 bar genişliği ayrılır, ki bu yalnız SABİT bir viewBox birimi — dosya başlığının S15a
  // notunun düzelttiği gibi PİKSEL aralığı konteynerin GERÇEK genişliğine göre küçülür (sr-only
  // tablo zaten tam veri kaynağı, M12).
  const visibleIndices = days.map((_, i) => i).filter((i) => (todayIndex - i) % 7 === 0);
  const firstVisibleIndex = visibleIndices[0];
  const lastVisibleIndex = visibleIndices[visibleIndices.length - 1];
  // **S36 #15a — dar konteynerde 5 etiket çakışıyordu.** Ölçüldü: DE "28. Sept." ~60px, 5
  // etiket × 60px = 300px > ölçülen dar-panel SVG genişliği (260–274px, ~1100px dashboard
  // içerik genişliğinde 2 sütunlu ızgaranın sağ paneli). İlk/orta/son HER ZAMAN görünür (3
  // etiket, en az yer kaplayan iskelet — 340px'te BİLE rahat sığdığı canlı QA ile doğrulandı);
  // aradaki 2 etiket yalnız konteyner ≥19rem (~304px, ölçülen "sığmıyor/sığıyor" eşiğinin ORTASI)
  // olunca bir `@container` sorgusuyla EKLENİR — rotasyon YOK, JS/ResizeObserver YOK.
  const middleVisibleIndex = visibleIndices[Math.floor(visibleIndices.length / 2)];
  const coreVisibleIndices = new Set([firstVisibleIndex, middleVisibleIndex, lastVisibleIndex]);

  return (
    <div className="flex flex-col gap-3 p-4">
      <p className="text-sm text-muted">
        {total > 0
          ? t('dashboard.chart.permitsOpened.summary', {
              count: total,
              date: formatDay(peak.date),
              peakCount: peak.count,
            })
          : t('dashboard.chart.permitsOpened.empty')}
      </p>

      <div className="flex gap-2">
        {/* Y ekseni — HTML gutter (M4), SVG'nin DIŞINDA (dosya başlığı). */}
        <div className="relative h-40 w-7 shrink-0">
          {[0, max / 2, max].map((tick) => (
            <span
              key={tick}
              className="tnum absolute right-0 -translate-y-1/2 text-xs text-muted"
              style={{ top: `${(yFor(tick) / VIEWBOX_HEIGHT) * 100}%` }}
            >
              {Math.round(tick)}
            </span>
          ))}
        </div>

        {/* `@container` (S36 #15a dosya başlığı) — X ekseni etiketlerinin gösterilecek SAYISI bu
            sarmalayıcının GERÇEK piksel genişliğine göre karar verilir, viewport'a DEĞİL (yan
            panel dar olsa bile geniş bir viewport'ta çakışma olurdu). */}
        <div className="@container min-w-0 flex-1">
          <div className="relative">
            <svg
              viewBox={`0 0 ${PLOT_WIDTH} ${VIEWBOX_HEIGHT}`}
              preserveAspectRatio="none"
              className="h-40 w-full"
              aria-hidden="true"
            >
              <title>{t('dashboard.chart.permitsOpened.title')}</title>
              {/* Izgara (3 çizgi: 0 / yarı / azami — design review "Gridlines: 3"). Metin
                  YOK (dosya başlığı) — yalnız `non-scaling-stroke` çizgi kalınlığını gerilmeden
                  bağımsız SABİT tutar. */}
              {[0, max / 2, max].map((tick) => {
                const y = yFor(tick);
                return (
                  <line
                    key={tick}
                    x1={0}
                    x2={PLOT_WIDTH}
                    y1={y}
                    y2={y}
                    className="stroke-border"
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
              {/* Çubuklar — sıfır günde bar YOK (taban çizgisi görünür kalır; design review "Zero
                  days: no bar"). */}
              {days.map((d, i) => {
                if (d.count === 0) {
                  return null;
                }
                const x = i * (barWidth + BAR_GAP);
                const y = yFor(d.count);
                const height = TOP_MARGIN + PLOT_HEIGHT - y;
                return (
                  <rect
                    key={d.date}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={height}
                    className="fill-dataviz-1"
                  >
                    <title>{`${formatDay(d.date)}: ${d.count}`}</title>
                  </rect>
                );
              })}
            </svg>
            {total === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-muted">{t('dashboard.chart.permitsOpened.empty')}</p>
              </div>
            ) : null}
          </div>

          {/* X ekseni — HTML satırı (M4), SVG'nin DIŞINDA. Kenar etiketleri İÇE büyür: ilk
              etiket sol kenarından (`translate-x-0`), son (bugün) sağ kenarından
              (`-translate-x-full`) başlar; aradakiler bar merkezine `-translate-x-1/2`
              hizalıdır. */}
          <div className="relative mt-1 h-4">
            {days.map((d, i) => {
              if ((todayIndex - i) % 7 !== 0) {
                return null;
              }
              const barLeft = i * (barWidth + BAR_GAP);
              const barRight = barLeft + barWidth;
              const isLast = i === lastVisibleIndex;
              const isFirst = !isLast && i === firstVisibleIndex;
              const left = isFirst ? barLeft : isLast ? barRight : barLeft + barWidth / 2;
              const translate = isFirst
                ? 'translate-x-0'
                : isLast
                  ? '-translate-x-full'
                  : '-translate-x-1/2';
              const isCore = coreVisibleIndices.has(i);
              return (
                <span
                  key={d.date}
                  className={`tnum absolute whitespace-nowrap text-xs text-muted ${translate} ${
                    isCore ? '' : 'hidden @[19rem]:inline'
                  }`}
                  style={{ left: `${(left / PLOT_WIDTH) * 100}%` }}
                >
                  {formatDay(d.date)}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Ekran okuyucu için TEK gerçek veri kaynağı (dosya başlığı, M12). `sr-only` SARMALAYICI
          `<div>`DE — doğrudan `<table>` üzerinde DEĞİL (final QA): `sr-only` `width:1px;height:1px`
          VERİR ama `table-layout:auto` bir `<table>` bunu YOK SAYAR, tarayıcı 30 satırı doğal
          içerik boyutunda (~334×768px) yerleştirir; `position:absolute` olduğu için bu gerçek
          kutu `clip-path` ile BOYANMASA da belge kaydırma alanını UZATIR (sayfanın altında ~700-
          900px ölü boşluk). Açıkça boyutlandırılmış (`width/height` sabit) bir `<div>` çocuğunun
          doğal boyutundan ETKİLENMEZ — `overflow:hidden` tabloyu İÇERİDE tutar, div'in kendisi
          1×1px kalır. */}
      <div className="sr-only">
        <table>
          <caption>{t('dashboard.chart.permitsOpened.title')}</caption>
          <thead>
            <tr>
              <th>{t('dashboard.chart.permitsOpened.col.date')}</th>
              <th>{t('dashboard.chart.permitsOpened.axis')}</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date}>
                <td>{formatDay(d.date)}</td>
                <td>{d.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
